import {
    Component,
    ElementRef,
    OnDestroy,
    OnInit,
    ViewChild,
} from '@angular/core';
import {
    animate,
    AnimationEvent,
    state,
    style,
    transition,
    trigger,
} from '@angular/animations';
import { ActivatedRoute, Params } from '@angular/router';
import { ProductService } from 'src/app/shared/services/product.service';
import { Store } from '@ngrx/store';
import { AppState } from 'src/app/viewbid/state/app-state';
import { TranslateService } from '@ngx-translate/core';
import {
    AuctionCatalogsService,
    AuctionDetail,
    GetWatcherByIdResponse,
    GetAuctionById200Response,
    BiddingService,
    ApplicationPreferencesResponse,
    GetLotFiltersResponse,
    GetLotFiltersResponseDataCategoriesInner,
    PostBidLotState,
} from '@viewbid/ts-angular-client';
import { VbSnackbarService } from '@viewbid/ui';
import { BiddingUtils, reconcileHighBidder } from '@/vb-utils/bidding.util';
import {
    addToWatchlist,
    removeFromWatchlist,
} from '../../../profiles/state/watchlist-page-state/watchlist-page-state.actions';
import {
    catchError,
    debounceTime,
    distinctUntilChanged,
    firstValueFrom,
    map,
    of,
    Subject,
    switchMap,
    takeUntil,
    tap,
} from 'rxjs';
import { DeviceCheckService } from 'src/app/viewbid/shared/services/device-check.service';
import { SocketService } from 'src/app/viewbid/shared/services/socket.service';
import { BsModalService } from 'ngx-bootstrap/modal';
import {
    activeFilterCount,
    Auction,
    AuctionBandState,
    EMPTY_FILTER,
    FilterValue,
    fmParseNum,
    isRangeInvalid,
    Lot,
    WatchlistAction,
} from '@viewbid/ui';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import {
    deriveAuctionBandState,
    firstLotSnipeWindowStart,
    isAuctionClosing,
} from '@/vb-utils/auction.util';
import {
    LotResponseWithProxy,
    auctionResponseToAuction,
    lotResponseToLot,
} from 'src/app/viewbid/shared/utils/lot-card-mapper.util';
import {
    LOT_CLOSED_LINGER_MS,
    LOT_GRACE_MS,
    LotCountdownTracker,
    hasTimePassed,
} from 'src/app/viewbid/shared/utils/lot-countdown.util';
import { scrollUpTo } from 'src/app/viewbid/shared/utils/scroll-up-to.util';
import { WatchlistService } from 'src/app/viewbid/shared/services/watchlist.service';
import {
    LotCardBidService,
    nextBidAmountFor,
} from 'src/app/viewbid/shared/services/lot-card-bid.service';
import { TimerService } from '@/shared/services/timerService';
import { ServerClockService } from 'src/app/viewbid/shared/services/server-clock.service';
import {
    AuctionLotPageCache,
    LotPageCacheEntry,
    LotPageQuery,
} from './auction-lot-page-cache';

const DEFAULT_SORTING: string = 'LOT_ORDER_ASC';

/** Upper bound of the random delay before a close-driven refetch. Every
 *  viewer sees the same close at the same second, so without it they all
 *  hit the API on one tick. */
export const CLOSE_REFETCH_JITTER_MS: number = 3000;

function closeRefetchDelayMs(): number {
    return Math.floor(Math.random() * CLOSE_REFETCH_JITTER_MS);
}

@Component({
    selector: 'app-auction-lot-list-page',
    templateUrl: './auction-lot-list-page.components.html',
    styleUrls: ['./auction-lot-list-page.component.scss'],
    animations: [
        // A lot that closes while "Auto-hide closed lots" is on is marked hidden;
        // its row fades + collapses so the remaining lots slide up into place
        // (then it's removed from the list). State-based — not :leave — so
        // pagination/filter list swaps stay instant and don't animate.
        trigger('lotRow', [
            state(
                'hidden',
                style({
                    height: '0',
                    opacity: 0,
                    marginTop: '0',
                    marginBottom: '0',
                    paddingTop: '0',
                    paddingBottom: '0',
                }),
            ),
            transition('visible => hidden', [
                style({ overflow: 'hidden' }),
                animate('280ms ease'),
            ]),
        ]),
    ],
})
export class AuctionLotListPageComponent implements OnInit, OnDestroy {
    /** The lot grid itself — measured for contentHoldHeight as a reload
     *  starts. Unmounted (undefined) while the content spinner is up. */
    @ViewChild('lotItems') private lotItems?: ElementRef<HTMLElement>;
    /** Search bar above the list — the scroll target when the reader picks a page. */
    @ViewChild('searchBar', { read: ElementRef })
    private searchBar?: ElementRef<HTMLElement>;

    public products: LotResponseWithProxy[] = [];
    public mappedLots: Lot[] = [];
    public mappedAuction: Auction = {};
    public sortingOptions: { value: string; label: string }[] = [];
    public pageNo: number = 1;
    public pageSize: number = 10;
    public readonly pageSizeOptions: ReadonlyArray<number> = [10, 25, 50];
    /** Rows requested while closed lots are hidden — that mode swaps the
     *  paginator for a "Show more" button that grows this window. Each load
     *  refetches page 1 at this size, so server-side page shifts from lots
     *  closing can't duplicate or skip rows. */
    private infiniteWindowSize: number = 10;
    /** True while a "Show more" fetch is in flight (list stays visible). */
    public isLoadingMore: boolean = false;
    public paginate: any = {};
    public sortBy: string = DEFAULT_SORTING;
    public isLoading: boolean = true;
    public unableToLoad: boolean = false;
    public auctionId: string = '';
    public auctionLotsId: string = '';
    /** Deep-linked lot still awaiting its scroll-into-view; consumed (one
     *  shot) by the first data commit after the route param lands. */
    private pendingScrollLotId: string | null = null;
    /** Height of the lot grid at the moment a content reload starts, held as
     *  the spinner container's min-height for the duration. Without it the
     *  document shortens by a page of cards, the browser clamps a deep scroll
     *  offset to the shorter page, and the viewport visibly bounces. */
    public contentHoldHeight: number | null = null;
    public auction?: AuctionDetail;
    // "Auto-hide closed lots" is off when the page opens (closed lots
    // visible) unless the auction is already in its closing phase; the
    // toolbar toggle flips it for the current visit only (deliberately not
    // persisted). While auto-hide is on, the list swaps pagination for a
    // growing "Show more" window. Internal state stays in "show" terms:
    // showClosedLots === !autoHide.
    public showClosedLots: boolean = true;
    /** One-shot per auction: once the closing phase auto-engages auto-hide,
     *  a user who flips it back off afterwards isn't fought again. Reset
     *  when the route lands on a different auction. */
    private autoHideAutoEngaged: boolean = false;
    // Lots whose row is animating out (closed while "Auto-hide closed lots"
    // is on). The row collapses, then onLotRowAnimDone() removes it from the
    // list.
    public hidingLotIds: Set<string> = new Set<string>();
    /** Grace+linger timers for lots whose clock hit zero while closed lots are
     *  hidden — pending row-hides, re-validated when they fire (an anti-snipe
     *  reopen or the "Auto-hide closed lots" toggle voids them). */
    private readonly pendingHideTimers: Map<
        string,
        ReturnType<typeof setTimeout>
    > = new Map();
    /** Jittered auto-hide engage for a closing phase seen live; a manual
     *  toggle or a different auction voids it. */
    private pendingAutoHideTimer: ReturnType<typeof setTimeout> | null = null;
    /** Jittered refill after the hide-closed window emptied; any newer
     *  load supersedes it. */
    private pendingRefillTimer: ReturnType<typeof setTimeout> | null = null;
    public isMobile: boolean = false;
    public activeTab: 'grid' | 'list' = 'list';
    /** True when the viewport is in the lot card's compact range (≤639px).
     *  Compact is an automatically-triggered view, not a user choice: it
     *  overrides the rendered view and hides the grid/list toggle. */
    public compactView: boolean = false;
    public currentUserId: string = '';
    public bidLoadingByLot: Record<string, boolean> = {};
    public userBidLotIds: Set<string> = new Set();
    /** True while a content-area reload (sort, filter, tab toggle, pagination)
     *  is in flight — keeps the page chrome visible, only the lot grid spins. */
    public isContentLoading: boolean = false;
    /** Flipped to true after the first successful load; subsequent loads use the
     *  content-area spinner instead of the full-page spinner. */
    private hasLoadedOnce: boolean = false;
    // ---- Info band (derived once per auction load/patch, see
    //      updateAuctionDerived) ----
    public auctionBandState: AuctionBandState = 'open';
    public paymentMethodLabels: string[] = [];
    public auctionDescriptionHtml: SafeHtml | null = null;
    public pickupAddressLine1: string | null = null;
    public pickupAddressLine2: string | null = null;
    public pickupDirectionsUrl: string | null = null;
    /** Both pickup types are offered on every auction today (mirrors the
     *  invoice delivery options); revisit when the API grows a per-auction
     *  self-service flag. */
    public pickupMethods: string[] = ['Regular', 'Self-service'];
    public termsOpen: boolean = false;
    public termsHtml: SafeHtml | string = '';
    /** Driven by the info band's own Hide/Show header. Per-visit state, like
     *  the auto-hide closed lots switch. */
    public infoBandHidden: boolean = false;

    public confirmDialogOpen: boolean = false;
    public pendingBidAmount: number = 0;
    private pendingBidLot: Lot | null = null;
    private pendingBidLocation: 'QUICK_BID' | 'MAIN_INPUT_FIELD' = 'QUICK_BID';

    public filterModalOpen: boolean = false;
    public filterDraft: FilterValue = { min: '', max: '', cats: [] };
    public appliedFilter: FilterValue = { min: '', max: '', cats: [] };
    public filterCategories: string[] = [];
    public filterCategoryCounts: Record<string, number> | undefined;
    public filterMatchCount: number | undefined;
    public filterTotalCount: number = 0;

    public countdownTracker: LotCountdownTracker;

    /** Shared closing grace window (ms), forwarded to each lot card. */
    public readonly graceMs: number = LOT_GRACE_MS;

    private destroy$ = new Subject<void>();
    private searchQuery?: string;
    private readonly lotPages: AuctionLotPageCache;
    private destroyed: boolean = false;
    /** Facet totals for the current price draft (category selection excluded). */
    private priceFilteredTotal: number | undefined;
    private facetsLoaded: boolean = false;
    private readonly filterFacets$ = new Subject<FilterValue>();

    constructor(
        private store: Store<AppState>,
        private route: ActivatedRoute,
        public productService: ProductService,
        public translate: TranslateService,
        private auctionService: AuctionCatalogsService,
        private snackbarService: VbSnackbarService,
        private utils: BiddingUtils,
        biddingService: BiddingService,
        private deviceCheckService: DeviceCheckService,
        private socketService: SocketService,
        private modalService: BsModalService,
        private watchlistService: WatchlistService,
        private lotCardBidService: LotCardBidService,
        private timerService: TimerService,
        private sanitizer: DomSanitizer,
        private serverClock: ServerClockService,
    ) {
        this.countdownTracker = new LotCountdownTracker(this.timerService, () =>
            this.serverClock.now(),
        );
        this.lotPages = new AuctionLotPageCache(
            this.auctionService,
            biddingService,
            this.socketService,
            this.timerService,
            (entry: LotPageCacheEntry, refresh: boolean): void =>
                this.commitPage(entry, refresh),
            (): void => {
                this.unableToLoad = true;
                this.isLoading = false;
                this.isLoadingMore = false;
                this.isContentLoading = false;
            },
        );
    }

    async ngOnInit(): Promise<void> {
        this.countdownTracker.start();

        // Seed page/sort from the URL up front so returning from a lot (back)
        // restores the page the user left, and the first load fetches it
        // directly instead of loading page 1 then correcting.
        const initialParams: Params = this.route.snapshot.queryParams;
        if (initialParams['page']) {
            this.pageNo = Number(initialParams['page']) || 1;
        }
        if (initialParams['sortBy']) {
            this.sortBy = initialParams['sortBy'];
        }

        await this.fetchSortingOptions();
        if (this.destroyed) return;

        this.deviceCheckService.isMobile$
            .pipe(takeUntil(this.destroy$))
            .subscribe((isMobile: boolean) => {
                this.isMobile = isMobile;
            });

        // The compact card view is its own automatically-triggered view, keyed
        // to the SAME viewport breakpoint as the card's compact CSS. It never
        // rewrites activeTab — the user's grid/list choice survives the window
        // shrinking and is restored verbatim when it widens again.
        this.deviceCheckService.isCompactCards$
            .pipe(takeUntil(this.destroy$))
            .subscribe((compact: boolean) => {
                this.compactView = compact;
            });

        this.utils
            .getCurrentUserId()
            .pipe(takeUntil(this.destroy$))
            .subscribe((currentUserId: string) => {
                const changed: boolean = this.currentUserId !== currentUserId;
                this.currentUserId = currentUserId;
                if (changed && this.lotPages.hasQuery) this.loadData();
            });

        this.route.params
            .pipe(
                tap((params: Params) => {
                    const changed: boolean =
                        this.auctionId !== params['auctionId'];
                    this.auctionId = params['auctionId'];
                    this.auctionLotsId = params['auctionLotId'];
                    this.pendingScrollLotId = params['auctionLotId'] ?? null;
                    if (changed) {
                        this.autoHideAutoEngaged = false;
                        this.clearPendingAutoHide();
                    }
                    if (changed && this.lotPages.hasQuery) {
                        this.lotPages.setAuctionResource(null);
                        this.loadData();
                    } else if (this.products.length && !this.isLoading) {
                        this.scrollPendingLotIntoView();
                    }
                }),
                map((params: Params): string => params['auctionId']),
                distinctUntilChanged(),
                switchMap((auctionId: string) =>
                    this.auctionService.getAuctionById(auctionId).pipe(
                        catchError(() => {
                            this.snackbarService.error(
                                this.translate.instant(
                                    'AuctionsPage.getAuctionDetailsError',
                                ),
                            );
                            return of(null);
                        }),
                    ),
                ),
                takeUntil(this.destroy$),
            )
            .subscribe((res: GetAuctionById200Response | null) => {
                if (!res) return;
                this.auction = res.data;
                this.mappedAuction = auctionResponseToAuction(this.auction);
                this.updateAuctionDerived();
                this.lotPages.setAuctionResource(this.auction.id ?? null);
                this.rebuildMappedLots();
            });
        this.route.queryParams
            .pipe(takeUntil(this.destroy$))
            .subscribe((queryParams: Params) => {
                const previousSort: string = this.sortBy;
                this.sortBy = this.getSafeSortBy(
                    queryParams['sortBy'] || DEFAULT_SORTING,
                );
                // React to URL page changes (browser back/forward). loadData's
                // getPager() replaces the URL in place when the page is
                // unchanged, so this can't re-trigger its own navigation.
                const page: number = Number(queryParams['page']) || 1;
                if (page !== this.pageNo || previousSort !== this.sortBy) {
                    this.pageNo = page;
                    this.loadData();
                }
            });

        this.filterFacets$
            .pipe(
                debounceTime(250),
                switchMap((draft: FilterValue) =>
                    this.auctionService
                        .getLotFiltersByAuctionId(
                            this.auctionId,
                            fmParseNum(draft.min) ?? undefined,
                            fmParseNum(draft.max) ?? undefined,
                            this.showClosedLots,
                        )
                        .pipe(
                            catchError(() =>
                                of<GetLotFiltersResponse | null>(null),
                            ),
                        ),
                ),
                takeUntil(this.destroy$),
            )
            .subscribe((response: GetLotFiltersResponse | null) => {
                this.applyFilterFacets(response);
            });
        if (this.auctionId != null) {
            this.filterFacets$.next(this.filterDraft);
        }

        this.socketService.resource$
            .pipe(takeUntil(this.destroy$))
            .subscribe((data: string) => {
                this.handleSocketEvent(data);
            });

        // Reconnect REST backstop: refetch the visible lot list. A cached
        // snapshot would defeat the point of resyncing after a dropped
        // connection, so drop the cache first and force a live fetch.
        this.socketService.reconnected$
            .pipe(takeUntil(this.destroy$))
            .subscribe(() => {
                this.lotPages.clear();
                this.loadData();
            });

        this.timerService.timerComplete$
            .pipe(takeUntil(this.destroy$))
            .subscribe((auctionLotId: string) => {
                this.onLotTimerComplete(auctionLotId);
            });

        // Client-clock "lot just ended" signal. timerComplete$ alone isn't
        // enough: TimerService only has a clock for a lot after a bid/snipe
        // socket event registered one, so a lot that runs out quietly would
        // otherwise sit closed until unrelated socket traffic mentioned it.
        this.countdownTracker.lotEnded$
            .pipe(takeUntil(this.destroy$))
            .subscribe((auctionLotId: string) => {
                this.onLotTimerComplete(auctionLotId);
            });

        this.timerService.startTimerComplete$
            .pipe(takeUntil(this.destroy$))
            .subscribe(() => {
                this.rebuildMappedLots();
            });

        this.timerService.snipeWindowTimerCompleteForAuction$
            .pipe(takeUntil(this.destroy$))
            .subscribe((auctionId: string) => {
                if (this.auction?.id === auctionId) {
                    this.updateAuctionDerived(true);
                }
            });

        this.loadData();
    }

    ngOnDestroy(): void {
        this.destroyed = true;
        this.destroy$.next();
        this.destroy$.complete();
        this.lotPages.destroy();
        this.products = [];
        this.userBidLotIds.clear();
        this.mappedLots = [];
        this.snackbarService.dismissAll();
        this.countdownTracker.stop();
        this.countdownTracker.setLots([]);
        this.pendingHideTimers.forEach((timer: ReturnType<typeof setTimeout>) =>
            clearTimeout(timer),
        );
        this.pendingHideTimers.clear();
        this.clearPendingAutoHide();
        this.clearPendingRefill();
    }

    /** The view actually rendered. Compact (≤639px viewport) is automatic and
     *  wins; otherwise the user's grid/list choice rules. */
    get effectiveTab(): 'grid' | 'list' {
        return this.compactView ? 'grid' : this.activeTab;
    }

    handleActiveTab(tab: 'grid' | 'list'): void {
        this.loadData();
        this.activeTab = tab;
    }

    /** Info-band inputs, recomputed whenever the auction record lands or a
     *  socket AUCTION_UPDATE patches it. `live` marks a broadcast or clock
     *  trigger that every viewer sees on the same tick. */
    private updateAuctionDerived(live: boolean = false): void {
        const auction: AuctionDetail | undefined = this.auction;
        if (!auction) return;
        this.auctionBandState = deriveAuctionBandState(auction);
        // Once bidding enters its closing phase, auto-hide closed lots so the
        // list narrows to what's still biddable. One-shot per auction — a
        // user who then flips it back off isn't fought again. Checked before
        // arming the closing timer, whose already-passed path re-enters here
        // synchronously as a live trigger.
        if (
            this.showClosedLots &&
            !this.autoHideAutoEngaged &&
            isAuctionClosing(auction, new Date(this.serverClock.now()))
        ) {
            this.autoHideAutoEngaged = true;
            if (live) {
                this.scheduleAutoHide(auction.id);
            } else {
                this.setShowClosedLots(false);
            }
        }
        const snipeWindowStart: Date | null = firstLotSnipeWindowStart(auction);
        if (snipeWindowStart) {
            this.timerService.notifySnipeWindowTimerCompleteForAuction(
                snipeWindowStart.toISOString(),
                auction.id,
            );
        }
        // Free-text column; lowercase legacy codes get a leading capital,
        // already-cased values pass through untouched.
        this.paymentMethodLabels = (auction.paymentTypes ?? [])
            .filter(Boolean)
            .map(
                (method: string) =>
                    method.charAt(0).toUpperCase() + method.slice(1),
            );
        this.auctionDescriptionHtml = auction.description
            ? this.sanitizer.bypassSecurityTrustHtml(auction.description)
            : null;
        const location: AuctionDetail['location'] | undefined =
            auction.location;
        if (location?.address1) {
            this.pickupAddressLine1 = [location.address2, location.address1]
                .filter(Boolean)
                .join(', ');
            this.pickupAddressLine2 = [
                location.city,
                [location.province, location.postalCode]
                    .filter(Boolean)
                    .join(' '),
            ]
                .filter(Boolean)
                .join(', ');
            const query: string = [
                location.address1,
                location.city,
                location.province,
                location.postalCode,
            ]
                .filter(Boolean)
                .join(', ');
            this.pickupDirectionsUrl =
                'https://www.google.com/maps/search/?api=1&query=' +
                encodeURIComponent(query);
        } else if (auction.pickupLocation) {
            this.pickupAddressLine1 = auction.pickupLocation;
            this.pickupAddressLine2 = null;
            this.pickupDirectionsUrl =
                'https://www.google.com/maps/search/?api=1&query=' +
                encodeURIComponent(auction.pickupLocation);
        } else {
            this.pickupAddressLine1 = null;
            this.pickupAddressLine2 = null;
            this.pickupDirectionsUrl = null;
        }
    }

    openTerms(): void {
        const terms: string | null | undefined = this.auction?.termsConditions;
        this.termsHtml = terms
            ? this.sanitizer.bypassSecurityTrustHtml(
                  terms.replace(
                      'BUYERS_PREMIUM',
                      String(this.auction?.buyerPremium ?? ''),
                  ),
              )
            : 'No terms and conditions available for this auction.';
        this.termsOpen = true;
    }

    get appliedFilterCount(): number {
        return activeFilterCount(this.appliedFilter);
    }

    openFilterModal(): void {
        this.filterDraft = {
            min: this.appliedFilter.min,
            max: this.appliedFilter.max,
            cats: [...this.appliedFilter.cats],
        };
        this.filterFacets$.next(this.filterDraft);
        this.filterModalOpen = true;
    }

    onFilterDraftChange(next: FilterValue): void {
        const priceChanged: boolean =
            next.min !== this.filterDraft.min ||
            next.max !== this.filterDraft.max;
        this.filterDraft = next;
        if (priceChanged) {
            // Counts are stale until the facets round-trip lands; the modal
            // CTA falls back to "Apply filter" while undefined.
            this.filterMatchCount = undefined;
            if (!isRangeInvalid(next)) {
                this.filterFacets$.next(next);
            }
            return;
        }
        this.recomputeFilterMatchCount();
    }

    onFilterApply(): void {
        this.appliedFilter = {
            min: this.filterDraft.min,
            max: this.filterDraft.max,
            cats: [...this.filterDraft.cats],
        };
        this.filterModalOpen = false;
        this.setPage(1);
    }

    onFilterClosed(): void {
        this.filterModalOpen = false;
    }

    clearFilters(): void {
        this.appliedFilter = { ...EMPTY_FILTER, cats: [] };
        this.setPage(1);
    }

    sortByFilter(value: string): void {
        this.sortBy = value;
        // reset infinite window size to original
        this.infiniteWindowSize = this.pageSize;
        // Re-sorting re-orders the whole result set, so the current page number
        // is no longer meaningful — jump back to the first page.
        this.pageNo = 1;
        this.paginate = this.productService.getPager(
            Number(this.paginate.totalItems),
            Number(+Number(this.pageNo)),
            this.pageSize,
            // No URL write here — only reset the paginator to page 1. The
            // load below writes page + sortBy once its own totals arrive;
            // navigating twice per sort change buys nothing.
            false,
            this.sortBy,
        );
        this.loadData();
    }

    /** A page picked in either paginator: back up to the search bar, then load. */
    onPageChange(page: number): void {
        scrollUpTo(this.searchBar?.nativeElement, !this.isMobile);
        this.setPage(page);
    }

    setPage(page: number): void {
        this.pageNo = page;
        this.infiniteWindowSize = this.pageSize;
        this.loadData();
    }

    /** Search shares the API query with sorting; the cache combines both. */
    onSearch(query: string): void {
        this.searchQuery = query || undefined;
        this.pageNo = 1;
        this.infiniteWindowSize = this.pageSize;
        this.loadData();
    }

    setPageSize(pageSize: number): void {
        this.pageSize = pageSize;
        this.pageNo = 1;
        this.infiniteWindowSize = pageSize;
        this.loadData();
    }

    /** Holds the empty state off while the jittered refill waits. */
    get refillPending(): boolean {
        return this.pendingRefillTimer !== null;
    }

    /** More open lots exist beyond the current window (hide-closed mode). */
    get hasMoreLots(): boolean {
        return this.products.length < (Number(this.paginate.totalItems) || 0);
    }

    showMoreLots(): void {
        if (this.isLoading || this.isLoadingMore) return;
        // Grow relative to what's on screen so each click yields a full
        // pageSize of new lots even after some rows have faded away.
        this.infiniteWindowSize = this.products.length + this.pageSize;
        this.loadData(true);
    }

    autoHideClosedLotsClicked(event: Event): void {
        const input: HTMLInputElement = event.target as HTMLInputElement;
        // The switch is the inverse of the internal state: checked = auto-hide.
        this.setShowClosedLots(!input.checked);
    }

    /** True when the list is empty because every lot of a closed auction is
     *  hidden by "Auto-hide closed lots" — the empty state then reads
     *  "auction has closed" instead of "no lots" (filters can't reveal
     *  anything either: a closed auction has only closed lots).
     *
     *  The API status lags the last close when the user watches the lots
     *  finish live (each row animates away and the list empties before a
     *  fresh auction record lands), so refine forward against the clock —
     *  the same effectiveState rule the info band applies to endsAt. */
    get emptyBecauseAuctionClosed(): boolean {
        if (this.showClosedLots) return false;
        if (this.auctionBandState === 'closed') return true;
        return hasTimePassed(this.auction?.toDatetime, this.serverClock.now());
    }

    /** Empty-state "View Closed Lots" CTA — flips auto-hide back off (the
     *  toolbar switch follows via its [checked]="!showClosedLots" binding). */
    viewClosedLots(): void {
        this.setShowClosedLots(true);
    }

    private setShowClosedLots(show: boolean): void {
        this.clearPendingAutoHide();
        this.showClosedLots = show;
        // Re-fetch lots and filter facets so the showClosed value takes effect
        // immediately. Either direction restarts from the top of the list.
        this.pageNo = 1;
        this.infiniteWindowSize = this.pageSize;
        this.loadData();
        this.filterFacets$.next(this.filterDraft);
    }

    handleLoadingComplete(): void {
        // No-op; vb-lot-card handles its own image loading state.
    }

    onWatchlistUpdated(action: WatchlistAction): void {
        const lot: LotResponseWithProxy | undefined = this.products.find(
            (p: LotResponseWithProxy) =>
                p.auctionLotsId === action.auctionLotsId,
        );
        const title: string | undefined = lot?.title;

        if (action.action === 'add') {
            this.watchlistService
                .addToWatchlist(this.auctionId, action.auctionLotsId, title)
                .pipe(takeUntil(this.destroy$))
                .subscribe({
                    next: (watchlistId: string) => {
                        this.applyWatchlistMutation(
                            action.auctionLotsId,
                            watchlistId,
                        );
                        this.store.dispatch(
                            addToWatchlist({
                                watchlistId: action.auctionLotsId,
                            }),
                        );
                    },
                });
            return;
        }

        if (action.action === 'remove' && action.watcherId) {
            this.watchlistService
                .removeFromWatchlist(action.watcherId, title)
                .then(() => {
                    if (this.destroyed) return;
                    this.applyWatchlistMutation(action.auctionLotsId, '');
                    this.store.dispatch(
                        removeFromWatchlist({
                            watchlistId: action.auctionLotsId,
                        }),
                    );
                    this.utils.removeFromWatchlist(action.auctionLotsId);
                });
        }
    }

    onQuickBidPlaced(lot: Lot): void {
        if (!this.currentUserId || !lot.auctionLotsId || !lot.auctionId) return;
        this.startBid(lot, nextBidAmountFor(lot), 'QUICK_BID');
    }

    onCustomBidPlaced(lot: Lot, amount: number): void {
        if (!this.currentUserId || !lot.auctionLotsId || !lot.auctionId) return;
        this.startBid(lot, amount, 'MAIN_INPUT_FIELD');
    }

    /** Mirrors the lot-details flow: stage the bid, then show the library
     *  confirm dialog unless the user opted out for this auction. */
    private startBid(
        lot: Lot,
        amount: number,
        location: 'QUICK_BID' | 'MAIN_INPUT_FIELD',
    ): void {
        if (!amount) return;
        this.pendingBidLot = lot;
        this.pendingBidAmount = amount;
        this.pendingBidLocation = location;
        if (this.utils.shouldShowBidConfirmation(this.auctionId)) {
            this.confirmDialogOpen = true;
        } else {
            this.placePendingBid();
        }
    }

    onBidConfirmed(dontAsk: boolean): void {
        if (dontAsk) {
            this.utils.setSkipBidConfirmation(this.auctionId);
        }
        this.confirmDialogOpen = false;
        this.placePendingBid();
    }

    onBidCancelled(): void {
        this.confirmDialogOpen = false;
        this.pendingBidLot = null;
        this.pendingBidAmount = 0;
    }

    onBidRounded(): void {
        this.snackbarService.system(
            this.translate.instant('Bidding.bidRoundedDown'),
        );
    }

    private placePendingBid(): void {
        const lot: Lot | null = this.pendingBidLot;
        const amount: number = this.pendingBidAmount;
        this.pendingBidLot = null;
        if (!this.currentUserId || !lot?.auctionLotsId || !lot.auctionId) {
            return;
        }
        // The clock hit zero (and the card entered its grace window) while the
        // confirm dialog was open — don't land a bid after the lot has ended.
        if (this.isLotBiddingEnded(lot)) {
            return;
        }
        this.setBidLoading(lot.auctionLotsId, true);
        this.lotCardBidService.placeConfirmedBid(
            {
                auctionId: lot.auctionId,
                auctionLotsId: lot.auctionLotsId,
                lotId: lot.lotId ?? lot.id,
                lotTitle: lot.title,
                lotNumber: lot.number,
                buyerPremium: this.auction?.buyerPremium ?? 0,
                amount,
                minimumBidAmount: nextBidAmountFor(lot),
                pageName:
                    this.effectiveTab === 'list' ? 'LOT_LIST' : 'LOT_GRID',
                pageLocation: this.pendingBidLocation,
            },
            this.currentUserId,
            (placed: boolean, lotState?: PostBidLotState) => {
                if (this.destroyed) return;
                this.setBidLoading(lot.auctionLotsId!, false);
                if (placed) {
                    if (lotState) {
                        this.applyLotState(
                            lot.auctionLotsId!,
                            lotState,
                            amount,
                        );
                    } else {
                        this.applyOptimisticHighBidder(
                            lot.auctionLotsId!,
                            amount,
                        );
                    }
                }
            },
        );
    }

    userHasBidOn(lot: Lot): boolean {
        if (!lot.auctionLotsId) return false;
        return (
            this.userBidLotIds.has(lot.auctionLotsId) ||
            (typeof lot.maximumProxyBid === 'number' && lot.maximumProxyBid > 0)
        );
    }

    isLotBiddingEnded(lot: Lot): boolean {
        if (!lot.auctionLotsId) return false;
        const product: LotResponseWithProxy | undefined = this.products.find(
            (p: LotResponseWithProxy) => p.auctionLotsId === lot.auctionLotsId,
        );
        return hasTimePassed(product?.toDatetime, this.serverClock.now());
    }

    trackByLot(_index: number, lot: Lot): string {
        return lot.auctionLotsId ?? lot.id ?? `${lot.lotId}`;
    }

    private setBidLoading(auctionLotsId: string, loading: boolean): void {
        this.bidLoadingByLot = {
            ...this.bidLoadingByLot,
            [auctionLotsId]: loading,
        };
    }

    private applyLotState(
        auctionLotsId: string,
        lotState: PostBidLotState,
        bidAmount: number,
    ): void {
        const existing: LotResponseWithProxy =
            this.lotPages.findLot(auctionLotsId) ?? {};
        this.applySocketPatch(auctionLotsId, {
            currentPrice: lotState.currentBid,
            minBidAmount: lotState.minimumNextBid,
            toDatetime: lotState.toDatetime,
            highBidderId: reconcileHighBidder(
                existing.highBidderId,
                this.currentUserId,
                lotState.isHighBidder,
            ),
            maximumProxyBid: Math.max(existing.maximumProxyBid ?? 0, bidAmount),
        });
        this.rebuildMappedLots();
    }

    private applyOptimisticHighBidder(
        auctionLotsId: string,
        bidAmount: number,
    ): void {
        const existing: LotResponseWithProxy =
            this.lotPages.findLot(auctionLotsId) ?? {};
        // Our standing max rose either way — surface it now so the pill's
        // "your max" is correct immediately, regardless of who leads.
        const newMax: number = Math.max(
            existing.maximumProxyBid ?? 0,
            bidAmount,
        );
        // When proxy bidding is on and another user already holds the high bid,
        // their bid may be backed by a hidden proxy max that instantly
        // out-counters ours. We can't know that max locally, so claiming the
        // high bid here flashes the card "winning" until the proxy's auto-
        // counter arrives (via PAGE_LOAD) and flips it to "losing" — the bug.
        // Leave the high bidder as-is and let the socket resolve it (the card
        // shows "losing" meanwhile, which is the honest state). In every other
        // case (no leader yet, or we already lead) the win is certain, so claim
        // it to avoid a spurious "losing" flash.
        const otherUserLeads: boolean =
            !!existing.highBidderId &&
            existing.highBidderId !== this.currentUserId;
        if (this.auction?.isProxyBiddingEnabled && otherUserLeads) {
            this.applySocketPatch(auctionLotsId, { maximumProxyBid: newMax });
            this.rebuildMappedLots();
            return;
        }
        this.applySocketPatch(auctionLotsId, {
            highBidderId: this.currentUserId,
            maximumProxyBid: newMax,
        });
        this.rebuildMappedLots();
    }

    private applyWatchlistMutation(
        auctionLotsId: string,
        watcherId: string,
    ): void {
        this.applySocketPatch(auctionLotsId, { watcherId });
        this.rebuildMappedLots();
    }

    /** Update modal facets from the lot-filters endpoint. `null` (fetch error)
     *  hides counts so the modal degrades to a plain "Apply filter" CTA. */
    private applyFilterFacets(response: GetLotFiltersResponse | null): void {
        if (!response?.data) {
            this.filterCategoryCounts = undefined;
            this.priceFilteredTotal = undefined;
            this.filterMatchCount = undefined;
            return;
        }
        const facets: GetLotFiltersResponseDataCategoriesInner[] =
            response.data.categories ?? [];
        if (!this.facetsLoaded) {
            // The chip set and catalogue total come from the first (unbounded)
            // fetch; later fetches only narrow the counts by the price draft.
            this.filterCategories = facets
                .map(
                    (facet: GetLotFiltersResponseDataCategoriesInner) =>
                        facet.category,
                )
                .filter((category: string | undefined): category is string =>
                    Boolean(category),
                );
            this.filterTotalCount = response.data.totalCount ?? 0;
            this.facetsLoaded = true;
        }
        const counts: Record<string, number> = {};
        for (const category of this.filterCategories) {
            counts[category] = 0;
        }
        for (const facet of facets) {
            if (facet.category) {
                counts[facet.category] = facet.count ?? 0;
            }
        }
        this.filterCategoryCounts = counts;
        this.priceFilteredTotal = response.data.totalCount;
        this.recomputeFilterMatchCount();
    }

    private recomputeFilterMatchCount(): void {
        if (
            isRangeInvalid(this.filterDraft) ||
            this.priceFilteredTotal === undefined
        ) {
            this.filterMatchCount = undefined;
            return;
        }
        if (!this.filterDraft.cats.length) {
            this.filterMatchCount = this.priceFilteredTotal;
            return;
        }
        const counts: Record<string, number> = this.filterCategoryCounts ?? {};
        this.filterMatchCount = this.filterDraft.cats.reduce(
            (sum: number, category: string) => sum + (counts[category] ?? 0),
            0,
        );
    }

    /** Cached and fetched pages share the pager/URL and visible-state commit. */
    private commitPage(
        entry: LotPageCacheEntry,
        refresh: boolean = false,
    ): void {
        this.paginate = this.productService.getPager(
            entry.totalItems,
            entry.page,
            entry.pageSize,
            this.showClosedLots,
            this.sortBy,
        );
        this.userBidLotIds = new Set(entry.userBidLotIds);
        this.products = entry.rows;
        this.unableToLoad = false;
        this.rebuildMappedLots();
        this.hasLoadedOnce = true;
        if (!refresh) {
            this.isLoading = false;
            this.isLoadingMore = false;
            this.isContentLoading = false;
            this.scrollPendingLotIntoView();
        }
    }

    private loadData(quiet: boolean = false): void {
        if (this.destroyed) return;
        this.clearPendingRefill();
        this.hidingLotIds.clear();
        this.unableToLoad = false;
        const query: LotPageQuery = {
            auctionId: this.auctionId,
            page: this.pageNo,
            pageSize: this.pageSize,
            sortBy: this.sortBy,
            searchQuery: this.searchQuery,
            filter: this.appliedFilter,
            showClosedLots: this.showClosedLots,
            windowSize: this.infiniteWindowSize,
            userId: this.currentUserId,
        };
        this.lotPages.load(query, (): void => {
            if (quiet) {
                this.isLoadingMore = true;
            } else if (!this.hasLoadedOnce) {
                this.isLoading = true;
            } else {
                // Keep the last height if another miss arrives while the grid is unmounted.
                this.contentHoldHeight =
                    this.lotItems?.nativeElement.offsetHeight ??
                    this.contentHoldHeight;
                this.isContentLoading = true;
            }
        });
    }

    private rebuildMappedLots(): void {
        // Closed-lot visibility is now decided server-side via the showClosed
        // query param (driven by the auto-hide toggle), so map every lot the
        // server returned. A lot that closes live stays until scheduleLotHide()
        // animates its row out (when auto-hide is on) and drops it.
        this.mappedLots = this.products.map((product: LotResponseWithProxy) =>
            lotResponseToLot(product),
        );

        this.countdownTracker.setLots(
            this.products
                .filter((p: LotResponseWithProxy) => !!p.auctionLotsId)
                .map((p: LotResponseWithProxy) => ({
                    auctionLotId: p.auctionLotsId!,
                    fromDatetime: this.auction?.fromDatetime,
                    toDatetime: p.toDatetime,
                })),
        );
        this.lotPages.syncCommitted(this.products, [...this.userBidLotIds]);
    }

    private applySocketPatch(
        auctionLotsId: string | undefined,
        patch: Partial<LotResponseWithProxy>,
    ): number {
        const idx: number = this.lotPages.patch(auctionLotsId, patch);
        const entry: LotPageCacheEntry | null = this.lotPages.currentPage;
        if (entry) {
            this.products = entry.rows;
            this.userBidLotIds = new Set(entry.userBidLotIds);
        }
        return idx;
    }

    /** Centres the deep-linked lot's card once the list has rendered. One
     *  shot: consumed on the first commit whether or not the lot is on this
     *  page, so later pagination can't trigger a surprise scroll. */
    private scrollPendingLotIntoView(): void {
        const lotId: string | null = this.pendingScrollLotId;
        if (!lotId) return;
        this.pendingScrollLotId = null;
        if (
            !this.products.some(
                (p: LotResponseWithProxy) => p.auctionLotsId === lotId,
            )
        ) {
            return;
        }
        // Next tick so the *ngFor has painted the cards before we measure.
        setTimeout(() => {
            if (this.destroyed) return;
            const cards: HTMLElement[] = Array.from(
                document.querySelectorAll<HTMLElement>('[data-auction-lot-id]'),
            );
            const card: HTMLElement | undefined = cards.find(
                (el: HTMLElement) => el.dataset['auctionLotId'] === lotId,
            );
            card?.scrollIntoView({ block: 'center', behavior: 'smooth' });
        });
    }

    private handleSocketEvent(data: string): void {
        if (this.destroyed) return;
        let event: any;
        try {
            event = JSON.parse(data);
        } catch {
            return;
        }

        if (
            event.action === 'AUCTION_UPDATE' &&
            this.auction &&
            event.data?.id &&
            event.data.id === this.auction.id
        ) {
            const auction: AuctionDetail = { ...this.auction, ...event.data };
            this.auction = auction;
            this.mappedAuction = auctionResponseToAuction(auction);
            this.updateAuctionDerived(true);
            this.rebuildMappedLots();
            return;
        }

        if (
            event.action === 'BID_INCREMENT_UPDATE' &&
            (!event.data || event.data === this.auctionId)
        ) {
            this.utils.showMinimumBidChangedDialog(this.modalService);
            return;
        }

        if (event.action === 'UPDATE_TIMES_EVENT') {
            // Anti-snipe fan-out: every open lot in the sniped lot's group
            // (or the whole auction when live) gets a new close time.
            // TimerService resets the countdowns, but rebuildMappedLots()
            // reseeds the tracker from products[].toDatetime — and
            // isLotBiddingEnded / scheduleLotHide gate on it too — so the
            // source of truth must take the new times as well, or the lots
            // snap back and close at their old time.
            const lots: { auctionLotId?: string; endDate?: string }[] =
                Array.isArray(event.data?.lots) ? event.data.lots : [];
            let touched: boolean = false;
            for (const lot of lots) {
                if (!lot.auctionLotId || !lot.endDate) continue;
                const lotIdx: number = this.applySocketPatch(lot.auctionLotId, {
                    toDatetime: lot.endDate,
                });
                if (lotIdx !== -1) touched = true;
            }
            if (
                this.auction &&
                event.data?.auction?.id === this.auction.id &&
                event.data.auction.endDate
            ) {
                this.auction = {
                    ...this.auction,
                    toDatetime: event.data.auction.endDate,
                };
                this.updateAuctionDerived(true);
                touched = true;
            }
            if (touched) this.rebuildMappedLots();
            return;
        }

        if (event.action === 'PAGE_LOAD' || event.action === 'NEW_HIGH_BID') {
            if (!event.data) return;
            const patch: Partial<LotResponseWithProxy> = {};
            if (event.data.endDate !== undefined)
                patch.toDatetime = event.data.endDate;
            if (event.data.minimumBid !== undefined)
                patch.minBidAmount = event.data.minimumBid;
            if (event.data.highBidder !== undefined)
                patch.highBidderId = event.data.highBidder ?? '';
            if (event.data.reserveAmount !== undefined)
                patch.reserveAmount = event.data.reserveAmount;
            if (event.data.currentPrice !== undefined)
                patch.currentPrice = event.data.currentPrice;
            // Don't take numberOfWatchers from PAGE_LOAD: it's computed before a
            // bid's auto-watch is added, so it's stale and would clobber the
            // authoritative WATCHER_COUNT_UPDATE broadcast. Socket payloads
            // serialize bigint counts as strings — coerce. Bid counts only ever
            // grow, so accept a socket count only when it RAISES the one we
            // already show: a closed lot's PAGE_LOAD can report 0 (or a stale
            // lower value), which would otherwise blank a real count to
            // "0 bids" until a refresh — the reported bug.
            const incomingBidCount: number = Number(event.data.bidCount);
            if (
                event.data.bidCount != null &&
                Number.isFinite(incomingBidCount) &&
                incomingBidCount >= 0
            ) {
                patch.bidCount = Math.max(
                    incomingBidCount,
                    this.lotPages.knownBidCount(event.data.auctionLotsId),
                );
            }
            const lotIdx: number = this.applySocketPatch(
                event.data.auctionLotsId,
                patch,
            );
            if (lotIdx !== -1) this.rebuildMappedLots();
            return;
        }

        if (event.action === 'USER_MAX_BID_UPDATE') {
            const lotIdx: number = this.applySocketPatch(
                event.data.auctionLotId,
                {
                    maximumProxyBid: Number(event.data.userMaxBid),
                    userMaxBid: Number(event.data.userMaxBid),
                },
            );
            if (lotIdx !== -1) this.rebuildMappedLots();
            return;
        }

        if (event.action === 'WATCHER_COUNT_UPDATE') {
            const lotIdx: number = this.applySocketPatch(
                event.data.auctionLotsId,
                { numberOfWatchers: event.data.numberOfWatchers },
            );
            if (lotIdx !== -1) this.rebuildMappedLots();
            return;
        }

        if (
            event.action === 'NEW_WATCHER_FROM_BID' &&
            event.data.accountMappingsId === this.currentUserId
        ) {
            const auctionLotId: string = event.data.auctionLotId;
            // A bid auto-creates a watcher the first time, so bump the header
            // watchlist count. Guard against re-counting if the socket replays
            // the event (e.g. on reconnect) — only count when not already watched.
            const alreadyWatching: boolean = this.utils.watchList.some(
                (w: GetWatcherByIdResponse) => w.auctionLotId === auctionLotId,
            );
            const lotIdx: number = this.applySocketPatch(auctionLotId, {
                watcherId: event.data.id,
            });
            this.utils.updateWatchlist(event.data, event.data.id);
            if (!alreadyWatching) {
                this.store.dispatch(
                    addToWatchlist({ watchlistId: auctionLotId }),
                );
            }
            if (lotIdx !== -1) this.rebuildMappedLots();
        }
    }

    private onLotTimerComplete(auctionLotId: string): void {
        const lotIdx: number = this.products.findIndex(
            (p: LotResponseWithProxy) => p.auctionLotsId === auctionLotId,
        );
        if (lotIdx === -1) return;
        // When closed lots are hidden, schedule the just-closed lot's row-hide
        // so it animates out (fade + collapse) only after the card has flipped
        // to "Closed" and lingered a beat; onLotRowAnimDone() then removes it.
        if (!this.showClosedLots) {
            this.scheduleLotHide(auctionLotId);
            return;
        }
        this.rebuildMappedLots();
    }

    /** Starts the row-hide countdown for a just-ended lot: waits out the
     *  card's pulsing-0s grace window plus a short linger so the bidder sees
     *  the "Closed" flip, then triggers the fade. Conditions are re-checked
     *  when the timer fires — an anti-snipe reopen, a toggle flip, or the lot
     *  leaving the list voids the hide. */
    private scheduleLotHide(auctionLotId: string): void {
        if (
            this.hidingLotIds.has(auctionLotId) ||
            this.pendingHideTimers.has(auctionLotId)
        ) {
            return;
        }
        const timer: ReturnType<typeof setTimeout> = setTimeout(() => {
            this.pendingHideTimers.delete(auctionLotId);
            if (this.showClosedLots) return;
            const product: LotResponseWithProxy | undefined =
                this.products.find(
                    (p: LotResponseWithProxy) =>
                        p.auctionLotsId === auctionLotId,
                );
            // Gone from the list, or reopened by an anti-snipe extension.
            if (!hasTimePassed(product?.toDatetime, this.serverClock.now())) {
                return;
            }
            this.hidingLotIds.add(auctionLotId);
        }, LOT_GRACE_MS + LOT_CLOSED_LINGER_MS);
        this.pendingHideTimers.set(auctionLotId, timer);
    }

    /** After a hidden lot's row finishes collapsing, drop it from the list so
     *  the remaining lots settle into place, and shrink the running total (it
     *  drives hasMoreLots). If the whole window just emptied but lots remain,
     *  quietly refill so the bidder isn't stranded on an empty list. */
    onLotRowAnimDone(lot: Lot, event: AnimationEvent): void {
        if (event.toState !== 'hidden') return;
        const id: string | undefined = lot.auctionLotsId;
        if (!id) return;
        this.products = this.products.filter(
            (p: LotResponseWithProxy) => p.auctionLotsId !== id,
        );
        this.hidingLotIds.delete(id);
        this.rebuildMappedLots();

        const remainingTotal: number = Math.max(
            (Number(this.paginate.totalItems) || 0) - 1,
            0,
        );
        this.paginate = { ...this.paginate, totalItems: remainingTotal };
        if (
            this.products.length === 0 &&
            remainingTotal > 0 &&
            this.pendingRefillTimer === null
        ) {
            this.pendingRefillTimer = setTimeout(() => {
                this.pendingRefillTimer = null;
                if (this.destroyed || this.products.length > 0) return;
                this.infiniteWindowSize = this.pageSize;
                this.loadData(true);
            }, closeRefetchDelayMs());
        }
    }

    /** Engages auto-hide after a random delay so viewers who all saw the
     *  closing phase begin on the same tick spread their refetches. */
    private scheduleAutoHide(auctionId: string | undefined): void {
        this.clearPendingAutoHide();
        this.pendingAutoHideTimer = setTimeout(() => {
            this.pendingAutoHideTimer = null;
            if (this.destroyed || this.auction?.id !== auctionId) return;
            if (!this.showClosedLots) return;
            this.setShowClosedLots(false);
        }, closeRefetchDelayMs());
    }

    private clearPendingAutoHide(): void {
        if (this.pendingAutoHideTimer === null) return;
        clearTimeout(this.pendingAutoHideTimer);
        this.pendingAutoHideTimer = null;
    }

    private clearPendingRefill(): void {
        if (this.pendingRefillTimer === null) return;
        clearTimeout(this.pendingRefillTimer);
        this.pendingRefillTimer = null;
    }

    private async fetchSortingOptions(): Promise<void> {
        try {
            const response: ApplicationPreferencesResponse =
                await firstValueFrom(
                    this.auctionService
                        .getApplicationPreferenceByKey(
                            'DEFAULT_LOT_SORT_FILTER_OPTIONS',
                        )
                        .pipe(takeUntil(this.destroy$)),
                );

            if (response.value) {
                try {
                    const options: { key: string; label: string }[] =
                        JSON.parse(response.value);
                    this.sortingOptions = options.map(
                        (opt: { key: string; label: string }) => ({
                            value: opt.key,
                            label: opt.label,
                        }),
                    );
                } catch {
                    this.sortingOptions = [];
                }
                return;
            }
            this.sortingOptions = [];
        } catch {
            if (this.destroyed) return;
            this.snackbarService.error(
                this.translate.instant('LotDetails.getSortingOptionsError'),
            );
        }
    }

    private getSafeSortBy(sortBy: string): string {
        return this.sortingOptions.some(
            (opt: { value: string }) => opt.value === sortBy,
        )
            ? sortBy
            : DEFAULT_SORTING;
    }
}
